import { Paragraph, TextRun, TableRow, AlignmentType, PageOrientation } from "docx";
import * as m from "@app/paraglide/messages.js";
import {
    createHeaderCell,
    createDataCell,
    createEmptyCell,
    createRow,
    formatTimecode,
    formatDuration,
    getEventNames,
    buildContentWithTags,
} from "../helpers.js";
import convertToFormattedTextRuns from "../convertToFormattedTextRuns.js";

/**
 * Technicolor CCSL template.
 * 5 columns, landscape orientation, no cell shading.
 * Dialogue events get their own row, then each spotting event gets a separate row.
 * Uses all four document sections (head, metadata, main, footer).
 */
export default {
    id: "technicolor",

    getDocumentConfig() {
        return {
            orientation: PageOrientation.LANDSCAPE,
            useSections: true,
            hasPageHeader: false
        };
    },

    /**
     * Returns the header row for the technicolor template.
     * @returns {TableRow[]}
     */
    getHeaders() {
        return [new TableRow({
            tableHeader: true,
            children: [
                createHeaderCell(m.ccsl_export_header_number_sign(), AlignmentType.LEFT),
                createHeaderCell(m.ccsl_export_header_continuity_dialogue(), AlignmentType.LEFT),
                createHeaderCell(m.ccsl_export_header_title(), AlignmentType.LEFT),
                createHeaderCell(m.ccsl_export_header_timecode(), AlignmentType.LEFT),
                createHeaderCell(m.ccsl_export_header_spotting(), AlignmentType.LEFT),
            ]
        })];
    },

    /**
     * Builds rows for a dialogue event with matching spotting events.
     * Pushes one row for the dialogue event, then one row per spotting event.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {Array} spottingEvents - Matching spotting events
     * @param {object} spottingEventGroup - Full spotting event group
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithSpotting(event, index, spottingEvents, spottingEventGroup, project) {
        const rows = [];

        // Dialogue event row (start time + tags + notes in column 2)
        rows.push(createRow([
            createDataCell((index + 1).toString()),
            createDataCell([
                new Paragraph(formatTimecode(event.start, project)),
                ...buildContentWithTags(event.tags, event.notes)
            ]),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
        ]));

        // One row per spotting event
        spottingEvents.forEach((spottingEvent) => {
            const spottingEventIndex = spottingEventGroup.events.findIndex(e => e.id === spottingEvent.id);
            const { speakerNames, recipientNames } = getEventNames(spottingEvent);

            // Build bold speaker label runs
            const speakerLabelRuns = [];
            if (speakerNames) {
                speakerLabelRuns.push(
                    new TextRun({
                        text: recipientNames
                            ? m.ccsl_export_speaker_to_recipient({ speakers: speakerNames, recipients: recipientNames })
                            : m.ccsl_export_speaker_only({ speakers: speakerNames }),
                        bold: true
                    }),
                    new TextRun({ break: 1 })
                );
            } else if (recipientNames) {
                speakerLabelRuns.push(
                    new TextRun({ text: m.ccsl_export_recipient_only({ recipients: recipientNames }), bold: true }),
                    new TextRun({ break: 1 })
                );
            }

            // Bold speaker-only label for the Continuity/Dialogue column
            const speakerOnlyLabelRuns = [];
            if (speakerNames) {
                speakerOnlyLabelRuns.push(
                    new TextRun({ text: m.ccsl_export_speaker_only({ speakers: speakerNames }), bold: true }),
                    new TextRun({ break: 1 })
                );
            }

            rows.push(createRow([
                // Empty ID column (spotting events don't get the main ID)
                createEmptyCell(),
                // Bold speaker names followed by the text
                createDataCell(
                    buildContentWithTags(spottingEvent.tags, new Paragraph({ children: [...speakerOnlyLabelRuns, ...convertToFormattedTextRuns(spottingEvent.text)] }))
                ),
                // Title column: "1/<index>."
                createDataCell(`1/${spottingEventIndex + 1}.`),
                // Timecode: start, end, duration on separate lines
                createDataCell([
                    new Paragraph(formatTimecode(spottingEvent.start, project)),
                    new Paragraph("\t" + formatTimecode(spottingEvent.end, project)),
                    new Paragraph("\t" + formatDuration(spottingEvent.start, spottingEvent.end, project))
                ]),
                // Speaker Name To Recipient + text (bold speaker names)
                createDataCell(
                    buildContentWithTags(spottingEvent.tags, new Paragraph({ children: [...speakerLabelRuns, ...convertToFormattedTextRuns(spottingEvent.text)] }))
                ),
            ]));
        });

        return rows;
    },

    /**
     * Builds a table row for a dialogue event without spotting events.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithoutSpotting(event, index, project) {
        return [createRow([
            createDataCell((index + 1).toString()),
            createDataCell([
                new Paragraph(formatTimecode(event.start, project)),
                ...buildContentWithTags(event.tags, event.notes)
            ]),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
        ])];
    }
};
